
'use client'

import { useAuth } from "@/components/providers/auth-provider";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { createCheckoutSession, createStripePortalSession, cancelSubscription } from "@/app/actions/stripe";
import { useToast } from "@/hooks/use-toast";
import { Check, ExternalLink, Loader2 } from "lucide-react";
import { useState } from "react";

const freeFeatures = [
    'Catalog up to 50 artworks',
    'Public and private artwork visibility',
    'Discover artworks from the community',
    'Basic collection management',
];

const proFeatures = [
    'Everything in the Free plan, plus:',
    'Unlimited artwork cataloging',
    'Advanced collection analytics',
    'Bulk import/export options (CSV, PDF)',
    'Priority support',
    'Early access to new features',
];


export function MembershipPlans() {
    const { user, userProfile } = useAuth();
    const { toast } = useToast();
    const [isSubscribing, setIsSubscribing] = useState(false);
    const [isCreatingPortal, setIsCreatingPortal] = useState(false);
    const [isDowngrading, setIsDowngrading] = useState(false);
    const isPro = userProfile?.roles.includes('pro');

    const handleSubscribe = async () => {
        if (!user) {
            toast({
                title: 'Not logged in',
                description: 'You must be logged in to subscribe.',
                variant: 'destructive'
            });
            return;
        }
        setIsSubscribing(true);
        try {
            await createCheckoutSession(user.uid, user.email || '');
        } catch (error) {
            console.error('Stripe checkout error:', error);
            toast({
                title: 'Error',
                description: 'Could not create a checkout session. Please try again.',
                variant: 'destructive'
            });
        } finally {
            setIsSubscribing(false);
        }
    }

     const handleManageBilling = async () => {
        if (!user || !userProfile?.stripeCustomerId) {
            toast({
                title: "Error",
                description: "Could not find your billing information. Please contact support.",
                variant: "destructive"
            });
            return;
        }

        setIsCreatingPortal(true);
        try {
            const session = await createStripePortalSession(userProfile.stripeCustomerId);
            window.location.href = session.url;
        } catch (error: any) {
            console.error("Stripe portal error:", error);
            toast({
                title: 'Error Opening Billing Portal',
                description: error.message || `An unexpected error occurred. Please try again or contact support.`,
                variant: 'destructive'
            });
        } finally {
            setIsCreatingPortal(false);
        }
    };
    
    const handleDowngrade = async () => {
        if (!user || !userProfile?.stripeSubscriptionId) {
            toast({
                title: "Error",
                description: "Could not find your subscription information. Please contact support.",
                variant: "destructive"
            });
            return;
        }
    
        setIsDowngrading(true);
        try {
            const result = await cancelSubscription(user.uid, userProfile.stripeSubscriptionId);
            
            if (result.success && result.cancelAt) {
                const cancelDate = new Date(result.cancelAt * 1000).toLocaleDateString();
                if(result.alreadyCanceled) {
                    toast({
                        title: 'Already Set to Downgrade',
                        description: `This subscription is already scheduled to downgrade on ${cancelDate}.`,
                    });
                } else {
                    toast({
                        title: 'Subscription Set to Downgrade',
                        description: `Your Vault Pro access will end on ${cancelDate}. You can undo this in the billing portal.`,
                    });
                }
            } else {
                 // Fallback for unexpected success states without data
                toast({
                    title: 'Subscription Status Updated',
                    description: 'Your subscription status has been updated.',
                });
            }
        } catch (error: any) {
            console.error("Downgrade error:", error);
            toast({
                title: 'Downgrade Failed',
                description: error.message || 'An unexpected error occurred during the downgrade.',
                variant: 'destructive',
            });
        } finally {
            setIsDowngrading(false);
        }
    };


    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Free Plan */}
            <Card>
                <CardHeader>
                    <CardTitle>Free Collector</CardTitle>
                    <CardDescription>For the casual collector getting started.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="text-4xl font-bold">$0<span className="text-lg font-normal text-muted-foreground">/month</span></div>
                    <ul className="space-y-2">
                        {freeFeatures.map((feature, index) => (
                            <li key={index} className="flex items-center">
                                <Check className="h-4 w-4 mr-2 text-green-500" />
                                <span>{feature}</span>
                            </li>
                        ))}
                    </ul>
                </CardContent>
                <CardFooter>
                   {isPro ? (
                       <Button variant="outline" className="w-full" onClick={handleDowngrade} disabled={isDowngrading}>
                            {isDowngrading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Downgrade
                        </Button>
                    ) : (
                         <Button className="w-full" disabled>
                            Your Current Plan
                        </Button>
                    )}
                </CardFooter>
            </Card>

            {/* Pro Plan */}
            <Card className={isPro ? "border-primary" : ""}>
                <CardHeader>
                        <div className="flex justify-between items-center">
                        <CardTitle>Vault Pro</CardTitle>
                    </div>
                    <CardDescription>For serious collectors and professionals.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="text-4xl font-bold">$4<span className="text-lg font-normal text-muted-foreground">/month</span></div>
                    <ul className="space-y-2">
                        {proFeatures.map((feature, index) => (
                            <li key={index} className="flex items-center">
                                <Check className="h-4 w-4 mr-2 text-primary" />
                                <span>{feature}</span>
                            </li>
                        ))}
                    </ul>
                </CardContent>
                    <CardFooter>
                    {isPro ? (
                            <Button className="w-full" onClick={handleManageBilling} disabled={isCreatingPortal}>
                                {isCreatingPortal ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin"/>
                                        Opening Portal...
                                    </>
                                ): (
                                    <>
                                        <ExternalLink className="mr-2 h-4 w-4"/>
                                        Manage Billing
                                    </>
                                )}
                            </Button>
                    ) : (
                        <Button className="w-full" onClick={handleSubscribe} disabled={isSubscribing}>
                            {isSubscribing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Upgrade to Pro
                        </Button>
                    )}
                </CardFooter>
            </Card>
        </div>
    )
}
